/**
 * This module provides the BatterySettings higher order component.
 *
 * It defines the layout of the settings sections.
 *
 * And it prepares the consumed form values. The injected form values will be updated as soon as an update request was
 * initiated. This is required to trigger the reactivity behaviour of Formik later on.
 *
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { PowerMeterModel } from 'src/Libs/power-meters'
import PowerMeterSettingsForm from 'src/Libs/power-meters/components/settings/PowerMeterSettingsForm'
import { SettingsFormValues } from 'src/Libs/power-meters/types'
import { makeStyles } from 'tss-react/mui'
import { FormikWithExitHandler } from 'src/Libs/form'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'
import SystemSettingsBlankView from 'src/Libs/system-settings/component/SystemSettingsBlankView'
import { usePowerMeterSettingsFormInitializer } from '../hooks'
import { modelToForm } from '../transformer'

const useStyles = makeStyles()(theme => ({
  contentAccordion: {
    padding: theme.spacing(0),
  },
  headerContent: {
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(4),
    },
  },
}))

interface Props {
  model?: PowerMeterModel
  disabled?: boolean
  onUpdate: (values: SettingsFormValues) => Promise<void>
  onChange?: (isDirty: boolean) => void
}

const PowerMeterSettings: React.FC<Props> = ({ model, disabled = false, onUpdate }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { initialValues, validationSchema } = usePowerMeterSettingsFormInitializer(model)
  const [formValues, setFormValues] = useState<SettingsFormValues>(initialValues)
  const { triggerAppRating } = useAppRatingContext()

  useEffect(() => {
    setFormValues(modelToForm(model))
  }, [model])

  const handleUpdate = useCallback(
    async (values: SettingsFormValues) => {
      try {
        setFormValues(values)
        await onUpdate(values)
        triggerAppRating()
      } catch (error) {
        /* revert changes */
        setFormValues({ ...formValues })
      }
    },
    [onUpdate, formValues, triggerAppRating]
  )

  return (
    <AccordionItem
      title={t('systemSettings.powerMeter.settings.sectionTitle')}
      contentClassName={classes.contentAccordion}
      headerContentClassName={classes.headerContent}
    >
      {model ? (
        <FormikWithExitHandler
          formKey="power-meter-settings-form"
          initialValues={formValues}
          validationSchema={validationSchema}
          onSubmit={handleUpdate}
        >
          <PowerMeterSettingsForm model={model} disabled={disabled} />
        </FormikWithExitHandler>
      ) : (
        <SystemSettingsBlankView />
      )}
    </AccordionItem>
  )
}

export default PowerMeterSettings
